import { isAxiosError, type AxiosError } from 'axios';
import { z } from 'zod';
import {
  AgentBuildingBlocksService,
  AgentDefinitionsService,
  type AgentDefinitionView,
  type AgentDefinitionVersionView,
  type BuildingBlockModel,
  type BuildingBlocksView,
  type UpdateAgentDefinitionDto,
} from '@inference.engine/api-client/web';
import { HostRequestError } from '@inference.engine/agentic-ui/host-errors';
import { widgetChangesSchema } from '@/entities/widget-definition';
import { inferenceHttp } from '@/lib/api/inference-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import type { AgentDefinitionDocument, DefinitionIssue, DefinitionScope } from '../model/types';

// The builder explains every failure where it happened, so the global toast would say it twice.
// eslint-disable-next-line i18next/no-literal-string -- header value
const quiet = { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' };

const issueSchema = z.object({
  code: z.string(),
  message: z.string(),
  kind: z.string().optional(),
  id: z.string().optional(),
  pointer: z.string().optional(),
  workspaceIds: z.array(z.string()).optional(),
  widgetName: z.string().optional(),
});

const problemSchema = z
  .object({
    code: z.string().optional(),
    detail: z.string().optional(),
    title: z.string().optional(),
    issues: z.array(issueSchema).optional(),
  })
  .loose();

function hostErrorParts(error: AxiosError): [message: string, code: string, details: unknown] {
  const { detail, code, status } = parseProblemDetail(error, error.message);
  const problem = problemSchema.safeParse(error.response?.data);
  return [
    detail,
    code ?? (status ? `http_${status}` : 'network_error'),
    problem.success ? problem.data : {},
  ];
}

/**
 * Rewraps a failed call as a {@link HostRequestError} carrying the API's stable `code`, so callers
 * branch on the reason (a conflict, a missing connection) rather than on status or message text.
 */
async function call<T>(request: () => Promise<unknown>): Promise<T> {
  try {
    return ((await request()) as { data: T }).data;
  } catch (error) {
    if (isAxiosError(error)) throw HostRequestError.fromCause(error, ...hostErrorParts(error));
    throw error;
  }
}

export const issuesOf = (error: unknown): DefinitionIssue[] => {
  if (!(error instanceof HostRequestError)) return [];
  const parsed = problemSchema.safeParse(error.details);
  return parsed.success ? (parsed.data.issues ?? []) : [];
};

const versionSummarySchema = z.object({
  definitionId: z.string(),
  version: z.number(),
  publishedBy: z.string(),
  publishedAt: z.string(),
  note: z.string().nullable(),
});
export type DefinitionVersionSummary = z.infer<typeof versionSummarySchema>;

const auditEventSchema = z.object({
  action: z.string(),
  actorUserId: z.string(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  occurredAt: z.string(),
});
export type DefinitionAuditEvent = z.infer<typeof auditEventSchema>;

const definitionListSchema = z.object({
  data: z.array(z.object({ id: z.string() }).loose()),
  nextCursor: z.string().nullable().optional(),
  hasMore: z.boolean().optional(),
});

const mcpToolsSchema = z.object({
  data: z.array(
    z.object({
      name: z.string(),
      description: z.string().optional(),
      readOnly: z.boolean().optional(),
    }),
  ),
});

export interface ScopeInput {
  scope: DefinitionScope;
  workspaceIds?: string[];
}

export const getDefinition = (id: string) =>
  call<AgentDefinitionView>(() =>
    AgentDefinitionsService.get({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      headers: quiet,
    }),
  );

export const listDefinitions = async (): Promise<AgentDefinitionView[]> => {
  const all: AgentDefinitionView[] = [];
  let cursor: string | undefined;
  do {
    const page = definitionListSchema.parse(
      await call<unknown>(() =>
        AgentDefinitionsService.list({
          client: inferenceHttp,
          throwOnError: true,
          query: { pageSize: 100, ...(cursor ? { cursor } : {}) },
        }),
      ),
    );
    all.push(...(page.data as unknown as AgentDefinitionView[]));
    cursor = page.hasMore ? (page.nextCursor ?? undefined) : undefined;
  } while (cursor);
  return all;
};

export const createDefinition = (
  input: ScopeInput & { document: AgentDefinitionDocument },
  idempotencyKey: string,
) =>
  call<AgentDefinitionView>(() =>
    AgentDefinitionsService.create({
      client: inferenceHttp,
      throwOnError: true,
      body: {
        scope: input.scope,
        ...(input.workspaceIds ? { workspaceIds: input.workspaceIds } : {}),
        document: input.document as unknown as Record<string, unknown>,
      },
      headers: { ...quiet, 'Idempotency-Key': idempotencyKey },
    }),
  );

export const saveDraft = (id: string, document: AgentDefinitionDocument, draftRevision: number) =>
  call<AgentDefinitionView>(() =>
    AgentDefinitionsService.saveDraft({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      body: { draftRevision, document: document as unknown as Record<string, unknown> },
      headers: quiet,
    }),
  );

export const validateDefinition = async (id: string): Promise<DefinitionIssue[]> => {
  const result = await call<unknown>(() =>
    AgentDefinitionsService.validate({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      headers: quiet,
    }),
  );
  return z.object({ issues: z.array(issueSchema) }).parse(result).issues;
};

const pendingWidgetSchema = z.object({
  id: z.string(),
  name: z.string(),
  nextVersion: z.number(),
  changes: widgetChangesSchema,
  otherAgents: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type PendingWidget = z.infer<typeof pendingWidgetSchema>;

/** The widget drafts on the canvas that publishing the agent publishes too. */
export const pendingWidgets = async (id: string): Promise<PendingWidget[]> => {
  const result = await call<unknown>(() =>
    AgentDefinitionsService.validate({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      headers: quiet,
    }),
  );
  return z.object({ widgets: z.array(pendingWidgetSchema).default([]) }).parse(result).widgets;
};

export const publishDefinition = (
  id: string,
  input: { note?: string; fromVersion?: number },
  idempotencyKey: string,
) =>
  call<AgentDefinitionVersionView>(() =>
    AgentDefinitionsService.publish({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      body: input,
      headers: { ...quiet, 'Idempotency-Key': idempotencyKey },
    }),
  );

export const listVersions = async (id: string): Promise<DefinitionVersionSummary[]> => {
  const result = await call<unknown>(() =>
    AgentDefinitionsService.listVersions({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      headers: quiet,
    }),
  );
  return z.object({ data: z.array(versionSummarySchema.loose()) }).parse(result).data;
};

export const getVersion = (id: string, version: number) =>
  call<AgentDefinitionVersionView>(() =>
    AgentDefinitionsService.getVersion({
      client: inferenceHttp,
      throwOnError: true,
      path: { id, version },
      headers: quiet,
    }),
  );

export const listAudit = async (id: string): Promise<DefinitionAuditEvent[]> => {
  const result = await call<unknown>(() =>
    AgentDefinitionsService.audit({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      headers: quiet,
    }),
  );
  return z.object({ data: z.array(auditEventSchema) }).parse(result).data;
};

export const updateDefinition = (id: string, body: UpdateAgentDefinitionDto) =>
  call<AgentDefinitionView>(() =>
    AgentDefinitionsService.update({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      body,
      headers: quiet,
    }),
  );

export const getBuildingBlocks = (input: ScopeInput) =>
  call<BuildingBlocksView>(() =>
    AgentBuildingBlocksService.list({
      client: inferenceHttp,
      throwOnError: true,
      query: {
        scope: input.scope,
        ...(input.workspaceIds?.length ? { workspaceIds: input.workspaceIds } : {}),
      },
      headers: quiet,
    }),
  );

/** The models an agent can run on, and the one it runs on when none is picked. */
export const getModelCatalog = (): Promise<{
  data: BuildingBlockModel[];
  defaultModelId?: string;
}> =>
  call(() =>
    AgentBuildingBlocksService.models({
      client: inferenceHttp,
      throwOnError: true,
      headers: quiet,
    }),
  );

const referenceFileSchema = z.object({ id: z.string(), fileName: z.string() }).loose();
export type ReferenceFile = z.infer<typeof referenceFileSchema>;

export const listReferenceFiles = async (id: string): Promise<ReferenceFile[]> =>
  z.array(referenceFileSchema).parse(
    await call<unknown>(() =>
      AgentDefinitionsService.listFiles({
        client: inferenceHttp,
        throwOnError: true,
        path: { id },
        headers: quiet,
      }),
    ),
  );

export const uploadReferenceFiles = (id: string, files: File[]) =>
  call<unknown>(() =>
    AgentDefinitionsService.uploadFiles({
      client: inferenceHttp,
      throwOnError: true,
      path: { id },
      body: { files },
      headers: quiet,
    }),
  );

export const deleteReferenceFile = (id: string, fileId: string) =>
  call<unknown>(() =>
    AgentDefinitionsService.removeFile({
      client: inferenceHttp,
      throwOnError: true,
      path: { id, fileId },
      headers: quiet,
    }),
  );

export const getMcpTools = async (serverId: string) => {
  const result = await call<unknown>(() =>
    AgentBuildingBlocksService.mcpTools({
      client: inferenceHttp,
      throwOnError: true,
      path: { id: serverId },
      headers: quiet,
    }),
  );
  return mcpToolsSchema.parse(result).data;
};
